import api from './api';
import { BUILD_MARKER } from '@/utils/buildInfo';
import { devLog } from '@/utils/logger';
import { getRecentSlowOpsString } from '@/utils/perfTelemetry';

/**
 * Client Telemetry Service
 *
 * Best-effort reporting of post-composer freezes (long tasks) and Lexical
 * editor errors so we can confirm on a real user's device whether a fix
 * worked, and capture a stack trace if a freeze/error ever recurs.
 *
 * This is purely observational: every call here MUST be non-blocking and
 * MUST NEVER throw into the UI. Failures (network, auth, rate limiting)
 * are swallowed silently (dev-only warning).
 */

export type ClientFreezeKind = 'longtask' | 'editor-error';

export interface ClientFreezePayload {
  kind: ClientFreezeKind;
  /** Length of the current caption/content at time of report */
  captionLength: number;
  /** Number of media items attached to the post at time of report */
  mediaCount: number;
  /** Duration of the long task in ms (kind: 'longtask') */
  longtaskMs?: number;
  /** Error.name (kind: 'editor-error') */
  errorName?: string;
  /** Error.message, truncated by the caller (kind: 'editor-error') */
  errorMessage?: string;
  /** Error.stack, truncated by the caller (kind: 'editor-error') */
  errorStack?: string;
  /**
   * Hottest main-thread call-stack captured via the JS Self-Profiling API
   * (see `src/utils/freezeProfiler.ts`) during the reported freeze, e.g.
   * "Top hot frames: ... \n Hottest stack (N samples): leaf < caller < ...".
   * Only present on Chromium with the `Document-Policy: js-profiling`
   * header; `undefined` everywhere else (unsupported browser, capture
   * failure, or no samples).
   */
  profileTop?: string;
}

/**
 * Full request body sent to the backend. `buildMarker`, `route`,
 * `userAgent`, `deviceMemory`, and `jsHeapMB` are auto-filled here so
 * callers only need to supply the event-specific fields above.
 */
interface ClientFreezeRequestBody extends ClientFreezePayload {
  buildMarker: string;
  route: string;
  userAgent?: string;
  deviceMemory?: number;
  jsHeapMB?: number;
  /**
   * Recent slow synchronous operations / renders captured by
   * `perfTelemetry.ts` (newest-first, "label:ms,label:ms,..."), for freezes
   * where the JS Self-Profiling API captured no stack (tight synchronous
   * loops have too few safepoints to sample). Only present when non-empty.
   */
  slowOps?: string;
}

/**
 * Narrow, non-standard `navigator.deviceMemory` (Device Memory API).
 * Not present in TypeScript's lib.dom.d.ts navigator typings, and not
 * supported by all browsers — accessed defensively below.
 */
interface NavigatorWithDeviceMemory extends Navigator {
  deviceMemory?: number;
}

/**
 * Narrow, non-standard `performance.memory` (Chrome/Blink only).
 * Not present in TypeScript's lib.dom.d.ts performance typings.
 */
interface PerformanceWithMemory extends Performance {
  memory?: {
    usedJSHeapSize: number;
    totalJSHeapSize: number;
    jsHeapSizeLimit: number;
  };
}

const MAX_USER_AGENT_LENGTH = 400;

// Module-level flood guards. Deliberately not persisted (per page-session
// only) — a hard page reload resets the counters, which is fine since a
// fresh page load is itself a natural reporting boundary.
const MIN_INTERVAL_MS = 10_000;
const MAX_EVENTS_PER_SESSION = 10;

let lastReportAt = 0;
let eventsReportedThisSession = 0;

const getDeviceMemory = (): number | undefined => {
  try {
    const nav = navigator as NavigatorWithDeviceMemory;
    return typeof nav.deviceMemory === 'number' ? nav.deviceMemory : undefined;
  } catch {
    return undefined;
  }
};

const getJsHeapMB = (): number | undefined => {
  try {
    const perf = performance as PerformanceWithMemory;
    if (perf.memory && typeof perf.memory.usedJSHeapSize === 'number') {
      return Math.round(perf.memory.usedJSHeapSize / 1048576);
    }
    return undefined;
  } catch {
    return undefined;
  }
};

/**
 * Report a post-composer freeze or Lexical editor error to the backend.
 *
 * Best-effort and fire-and-forget: rate-limited (max ~1 per 10s, max 10 per
 * page session), and guaranteed never to throw or reject into the caller —
 * every failure path (flood guard, browser API absence, network error) is
 * swallowed here.
 */
export const reportClientFreeze = (payload: ClientFreezePayload): void => {
  try {
    const now = Date.now();

    if (eventsReportedThisSession >= MAX_EVENTS_PER_SESSION) {
      return;
    }
    if (now - lastReportAt < MIN_INTERVAL_MS) {
      return;
    }

    lastReportAt = now;
    eventsReportedThisSession += 1;

    const slowOps = getRecentSlowOpsString();

    const body: ClientFreezeRequestBody = {
      ...payload,
      buildMarker: BUILD_MARKER,
      route: window.location.pathname,
      userAgent: navigator.userAgent?.slice(0, MAX_USER_AGENT_LENGTH),
      deviceMemory: getDeviceMemory(),
      jsHeapMB: getJsHeapMB(),
      slowOps: slowOps || undefined,
    };

    api.post('/api/v2/telemetry/client-freeze', body).catch((error: unknown) => {
      devLog.warn('[Telemetry] client-freeze report failed (non-fatal):', error);
    });
  } catch (error) {
    // Telemetry must never break the app it's observing.
    devLog.warn('[Telemetry] reportClientFreeze threw while preparing report (swallowed):', error);
  }
};
